import React, { useContext, useEffect, useMemo, useState } from "react";
import { BookOpenCheck, Users, BrainCircuit, Trophy } from "lucide-react";
import { AuthContext } from "../../context/AuthContext";
import axios from "../../api/axios";
import {
  masterContainer,
  masterAmbientGradient1,
  masterAmbientGradient2,
  masterScrollArea,
  masterPanelCard,
  masterGlowGradientsTop,
  masterGlowGradientsBottom,
  masterBadgeControl,
  masterHeadingMain,
  masterStickyHeadRow,
  masterTableRow,
  masterEmptyState,
  masterCardBox,
  masterCardIconBox,
  masterCardTextBox,
  masterCardTitle,
  masterCardLabel,
  masterWelcomeTitle,
  masterWelcomeSubtitle,
  masterTableCodeCell,
  masterTableNameCell,
  masterTableBadgeActive,
  masterTableDateCell,
} from "../../GlobalComponents/MasterCssClass";

const formatScore = (value) => Number(value || 0).toFixed(3);
const formatDuration = (sec) => {
  const s = Number(sec || 0);
  if (s <= 0) return "0s";
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return m > 0 ? `${m}m ${rem}s` : `${rem}s`;
};

// Custom DD-MM-YYYY Date Formatter with 12-hour time
const formatDate = (dateString) => {
  if (!dateString) return "---";
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return "---";

  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();

  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;

  return `${day}-${month}-${year}, ${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
};

const OrganizationDashboard = () => {
  const { user } = useContext(AuthContext);
  const [orgName, setOrgName] = useState(user?.orgName || "Organization");
  const [orgLogo, setOrgLogo] = useState("");
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const [exams, setExams] = useState([]);
  const [students, setStudents] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [selectedExamCode, setSelectedExamCode] = useState("ALL");

  useEffect(() => {
    const loadOrgDashboardData = async () => {
      try {
        const res = await axios.get("/master/org-dashboard/dashboard-data");
        if (res.data) {
          if (res.data.organizationName) setOrgName(res.data.organizationName);
          if (res.data.organizationDetails?.logo) setOrgLogo(res.data.organizationDetails.logo);
          if (Array.isArray(res.data.exams)) setExams(res.data.exams);
          if (Array.isArray(res.data.students)) setStudents(res.data.students);
          if (Array.isArray(res.data.attempts)) setAttempts(res.data.attempts);
        }
      } catch (err) {
        console.error("Organization dashboard fetch error:", err);
      }
    };

    loadOrgDashboardData();
  }, []);

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = async () => {
        const canvas = document.createElement("canvas");
        const MAX_WIDTH = 200;
        const MAX_HEIGHT = 200;
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > MAX_WIDTH) {
            height *= MAX_WIDTH / width;
            width = MAX_WIDTH;
          }
        } else {
          if (height > MAX_HEIGHT) {
            width *= MAX_HEIGHT / height;
            height = MAX_HEIGHT;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(async (blob) => {
          const resizedFile = new File([blob], file.name, {
            type: file.type,
            lastModified: Date.now(),
          });

          const formData = new FormData();
          formData.append("logo", resizedFile);

          setUploadingLogo(true);
          try {
            const res = await axios.post("/master/org-dashboard/update-logo", formData, {
              headers: { "Content-Type": "multipart/form-data" },
            });
            if (res.data && res.data.logo) {
              setOrgLogo(res.data.logo);
              alert("Organization logo resized and updated successfully!");
            }
          } catch (err) {
            alert(err.response?.data?.message || "Failed to upload logo");
          } finally {
            setUploadingLogo(false);
          }
        }, file.type, 0.9);
      };
    };
  };

  // Filter attempts based on selected exam dropdown
  const filteredAttempts = useMemo(() => {
    if (selectedExamCode === "ALL") return attempts;
    return attempts.filter(
      (att) => String(att.examCode || "").toUpperCase() === String(selectedExamCode).toUpperCase()
    );
  }, [attempts, selectedExamCode]);

  // Compute rankings sorted by highest obtained marks, then lowest time taken
  const rankedAttempts = useMemo(() => {
    return [...filteredAttempts].sort((a, b) => {
      const scoreDiff = Number(b.obtainedMarks || 0) - Number(a.obtainedMarks || 0);
      if (scoreDiff !== 0) return scoreDiff;
      return Number(a.timeTakenSeconds || 0) - Number(b.timeTakenSeconds || 0);
    });
  }, [filteredAttempts]);

  return (
    <div className={masterContainer}>
      <div className={masterAmbientGradient1} />
      <div className={masterAmbientGradient2} />
      
      <div className={masterScrollArea}>
        
        {/* Top Welcome Panel */}
        <div className={masterPanelCard}>
          <div className={masterGlowGradientsTop}></div>
          <div className={masterGlowGradientsBottom}></div>
          
          <div className="relative z-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            {/* Logo and Headings Section */}
            <div className="flex items-start sm:items-center gap-3.5">
              <div className="relative group h-16 w-16 sm:h-20 sm:w-20 overflow-hidden rounded-full border-2 border-slate-200 bg-white flex items-center justify-center shadow-md shrink-0">
                {orgLogo ? (
                  <img src={orgLogo} alt="Org Logo" className="h-full w-full object-cover" />
                ) : (
                  <span className="text-xs font-black tracking-wider text-slate-400">LOGO</span>
                )}
              </div>

              <div className="flex flex-col justify-center min-w-0 space-y-0.5">
                <p className={`${masterBadgeControl} w-fit text-[9px] sm:text-[10px] px-2 py-0.5 rounded-md`}>
                  Dashboard
                </p>
                <h1 className={`${masterWelcomeTitle} text-sm sm:text-lg font-extrabold tracking-tight text-slate-900 truncate`}>
                  Welcome, <span className={masterWelcomeSubtitle}>{user?.name || "Administrator"}</span>
                </h1>
                <div>
                  <label className="inline-flex items-center gap-1 rounded-lg bg-[#0E3652] hover:bg-[#154668] text-white px-3 py-1 text-[10px] font-semibold transition cursor-pointer shadow-xs">
                    {uploadingLogo ? "Uploading..." : "Change Logo"}
                    <input type="file" accept="image/*" onChange={handleLogoChange} className="hidden" disabled={uploadingLogo} />
                  </label>
                </div>
              </div>
            </div>

            {/* Exam Filter Dropdown Section */}
            <div className="w-full sm:w-auto pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-200">
              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                Filter by Exam Ranking
              </label>
              <select
                value={selectedExamCode}
                onChange={(e) => setSelectedExamCode(e.target.value)}
                className="w-full sm:w-[220px] rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-800 shadow-xs outline-none cursor-pointer"
              >
                <option value="ALL">All Exams</option>
                {exams.map((exam) => (
                  <option key={exam._id || exam.examCode} value={exam.examCode}>
                    {exam.examName || exam.name || exam.examCode}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Micro Metrics Grid */}
        <div className="grid grid-cols-3 gap-1.5 shrink-0">
          <div className={masterCardBox}>
            <div className={masterCardIconBox}>
              <BookOpenCheck size={14} strokeWidth={2.5} />
            </div>
            <div className={masterCardTextBox}>
              <p className={masterCardTitle}>{exams.length}</p>
              <p className={masterCardLabel}>Total Exams</p>
            </div>
          </div>

          <div className={masterCardBox}>
            <div className={masterCardIconBox}>
              <Users size={14} strokeWidth={2.5} />
            </div>
            <div className={masterCardTextBox}>
              <p className={masterCardTitle}>{students.length}</p>
              <p className={masterCardLabel}>Total Students</p>
            </div>
          </div>

          <div className={masterCardBox}>
            <div className={masterCardIconBox}>
              <BrainCircuit size={14} strokeWidth={2.5} />
            </div>
            <div className={masterCardTextBox}>
              <p className={masterCardTitle}>{attempts.length}</p>
              <p className={masterCardLabel}>Total Attempts</p>
            </div>
          </div>
        </div>

        {/* Mock Test Rankings / Results List Section */}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-300/80 bg-white p-2.5 shadow-xs">
          <div className="mb-1.5 flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-slate-200 pb-1.5 gap-1">
            <div className="flex items-center gap-2">
              <Trophy size={16} className="text-[#F4B429] shrink-0" />
              <h2 className={`${masterHeadingMain} text-xs sm:text-sm truncate`}>
                {selectedExamCode === "ALL" ? "Mock Test Rankings (All Exams)" : `Rankings for ${selectedExamCode}`}
              </h2>
            </div>
            <span className="text-[10px] font-bold text-slate-500">Total Submissions: {rankedAttempts.length}</span>
          </div>

          <div className="custom-scrollbar min-h-0 flex-1 overflow-x-auto overflow-y-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-left border-collapse min-w-[700px]">
              <thead>
                <tr className={masterStickyHeadRow}>
                  <th className="p-1.5 pl-2.5 text-center">Rank</th>
                  <th className="p-1.5">Student Name / Email</th>
                  <th className="p-1.5">Exam Name</th>
                  <th className="p-1.5">Subject</th>
                  <th className="p-1.5 text-center">Correct / Wrong</th>
                  <th className="p-1.5">Score</th>
                  <th className="p-1.5 text-center">Time Taken</th>
                  <th className="p-1.5 pr-2.5 text-right">Submitted At</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-transparent">
                {rankedAttempts.length > 0 ? rankedAttempts.map((row, idx) => (
                  <tr key={`${row.questionSetId}-${row.userEmail}-${idx}`} className={masterTableRow}>
                    <td className="p-1.5 text-center font-black text-slate-900 text-xs">
                      {idx === 0 ? "🥇 1" : idx === 1 ? "🥈 2" : idx === 2 ? "🥉 3" : `#${idx + 1}`}
                    </td>
                    <td className={masterTableNameCell}>
                      <p className="font-bold text-slate-900">{row.userName || "Student"}</p>
                      <p className="text-[10px] font-normal text-slate-500">{row.userEmail || "---"}</p>
                    </td>
                    <td className={masterTableCodeCell}>{row.examName || row.examCode || "---"}</td>
                    <td className="p-1.5 text-xs text-slate-700">{row.subjectName || "---"}</td>
                    <td className="p-1.5 text-center font-bold text-[11px]">
                      <span className="text-emerald-700 bg-emerald-50 px-1 py-0.5 rounded border border-emerald-200">{row.correctAnswers || 0}</span>
                      <span className="mx-0.5 font-normal text-slate-400">/</span>
                      <span className="text-rose-700 bg-rose-50 px-1 py-0.5 rounded border border-rose-200">{row.wrongAnswers || 0}</span>
                    </td>
                    <td className="p-1.5 font-bold text-slate-900 text-[11px]">
                      {formatScore(row.obtainedMarks)} <span className="font-normal text-slate-400">/</span> {formatScore(row.totalMarks)}
                    </td>
                    <td className="p-1.5 text-center font-mono text-[10px] font-semibold text-slate-600">
                      {formatDuration(row.timeTakenSeconds)}
                    </td>
                    <td className={masterTableDateCell}>
                      {formatDate(row.submittedAt)}
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="8" className={masterEmptyState}>
                      No test attempts found matching this filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
};

export default OrganizationDashboard;